/**
 * LeftToolRail Component
 *
 * Vertical tool rail for switching between tree visualization modes.
 * Provides quick access to different views: vertical tree, fan chart, timeline, map.
 */

import { useTranslation } from 'react-i18next';
import type { TreeVisualizationMode } from '../types';

interface LeftToolRailProps {
  activeMode: TreeVisualizationMode;
  onModeChange: (mode: TreeVisualizationMode) => void;
}

interface ToolButton {
  mode: TreeVisualizationMode;
  icon: string;
  labelKey: string;
}

export function LeftToolRail({ activeMode, onModeChange }: LeftToolRailProps) {
  const { t } = useTranslation('trees');

  const toolButtons: ToolButton[] = [
    { mode: 'vertical', icon: 'account_tree', labelKey: 'tools.verticalTree' },
    // Temporarily hidden - will be activated soon
    // { mode: 'fan', icon: 'pie_chart', labelKey: 'tools.fanChart' },
    // { mode: 'timeline', icon: 'schedule', labelKey: 'tools.timeline' },
    // { mode: 'map', icon: 'map', labelKey: 'tools.map' },
  ];

  return (
    <aside className="w-14 md:w-16 lg:w-20 h-full border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-background flex flex-col items-center py-3 md:py-4 lg:py-6 gap-4 md:gap-6 lg:gap-8 z-40 shadow-xl md:shadow-none">
      {/* Mobile Header - Hidden on desktop */}
      <div className="md:hidden w-full px-2 mb-1">
        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">View</p>
      </div>

      {/* Tool Buttons - Compact on mobile */}
      <div className="flex flex-col gap-2 md:gap-3 lg:gap-4 px-1 md:px-2 lg:px-0">
        {toolButtons.map((tool) => {
          const label = t(tool.labelKey as any);
          return (
            <button
              key={tool.mode}
              onClick={() => onModeChange(tool.mode)}
              className={`
                size-10 md:size-11 lg:size-12 rounded-lg md:rounded-xl flex items-center justify-center transition-all
                ${
                  activeMode === tool.mode
                    ? 'bg-primary text-white shadow-lg shadow-primary/20'
                    : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300'
                }
              `}
              title={label as string}
              aria-label={label as string}
            >
              <span className="material-symbols-outlined text-lg md:text-xl lg:text-2xl">{tool.icon}</span>
            </button>
          );
        })}
      </div>

      {/* Settings Button (Bottom) - Temporarily hidden */}
      {/* <div className="mt-auto">
        <button
          className="size-12 rounded-xl text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
          aria-label="Settings"
          title="Settings"
        >
          <span className="material-symbols-outlined">settings</span>
        </button>
      </div> */}
    </aside>
  );
}
